import { Injectable, OnDestroy } from '@angular/core';
import { Params, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { Observable, pairwise, ReplaySubject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';

import { GlobalBookingTravelClass } from '@fcom/core';
import { LanguageService } from '@fcom/ui-translate';
import { getBookingStepLink } from '@fcom/common/utils/booking-common.utils';
import {
  LocationPair,
  GlobalBookingActions,
  globalBookingPaxAmount,
  globalBookingSelections,
  GlobalBookingState,
  globalBookingTravelClasses,
  globalBookingTripType,
} from '@fcom/common/store';
import { globalBookingDates, globalBookingRouteType } from '@fcom/common/store/selectors/global-booking.selectors';
import { GlobalBookingTripDates, OfferListFetchParams, Step } from '@fcom/common/interfaces';
import { PaxAmount } from '@fcom/dapi';
import { LocationsService } from '@fcom/locations';
import { finShare, snapshot } from '@fcom/rx';
import { equals, unique, unsubscribe } from '@fcom/utils';
import { BookingTripLocations } from '@fcom/common-booking';
import { TripType } from '@fcom/core/constants';
import { LocationRouteType } from '@fcom/core-api/interfaces';
import { AVAILABLE_TRIP_TYPES } from '@fcom/booking-widget/constants';
import { BOOKING_STEPS, BOOKING_STEPS_ARRAY } from '@fcom/common/config/booking-config';
import { BookingQueryParams } from '@fcom/common/interfaces/booking-step.interface';

import { BookingActions, CartActions } from '../store/actions';
import { BookingAndAppState, BookingFlow, BookingLocationCodes, TravelClasses } from '../interfaces';
import {
  bookingFlow,
  bookingLocationCodes,
  bookingLocations,
  bookingStep,
  lastRequestParams,
  offerLastRequestParams,
  selectFareFamiliesBrands,
} from '../store/selectors';

export interface StepChangeSignature {
  onSuccessPost?: (step: Step) => any;

  /**
   * If this is set to true, will not call router.navigate
   */
  skipRouteChange?: boolean;
}

export interface SetStepSignature extends StepChangeSignature {
  step: Step;
}

interface TranslationKeyFareFamilyBransDictiounaryItem {
  translationKey: string;
  brandNames: string[];
}

const noop = () => {};

export const createParamsForStep = (
  step: Step,
  flow: BookingFlow,
  common: GlobalBookingState,
  offerListParams: OfferListFetchParams
): { json?: string } => {
  if (step.id !== BOOKING_STEPS.FLIGHT_SELECTION.id) {
    return null;
  }

  /**
   * If the session has expired and there is no longer a search query in the store then don't try
   * and encode any "initialState" data into the url.
   **/
  if (!flow?.locations?.length) {
    return null;
  }

  const flightSelectionParams = {
    flights: flow?.locations?.map((location, index) => ({
      origin: location.originLocationCode,
      destination: location.destinationLocationCode,
      departureDate: index === 0 ? common?.dates?.departureDate?.toString() : common?.dates?.returnDate?.toString(),
    })),
    cabin: common?.travelClasses?.selected,
    adults: common?.paxAmount?.adults,
    c15s: common?.paxAmount?.c15s,
    children: common?.paxAmount?.children,
    infants: common?.paxAmount?.infants,
    departureDate: common?.dates?.departureDate?.toString(),
    returnDate: common?.tripType === TripType.RETURN ? common?.dates?.returnDate?.toString() : null,
    directFlights: offerListParams?.directFlights,
    isAward: offerListParams?.isAward || null,
  };

  return {
    json: encodeURIComponent(JSON.stringify(flightSelectionParams)),
  };
};

@Injectable()
export class BookingService implements OnDestroy {
  private readonly booking$: Observable<BookingFlow>;
  private readonly locations$: Observable<BookingTripLocations[]>;
  private readonly dates$: Observable<GlobalBookingTripDates>;
  private readonly step$: Observable<Step>;
  private readonly previousStep$: ReplaySubject<Step> = new ReplaySubject(2);
  private readonly paxAmount$: Observable<PaxAmount>;
  private readonly tripType$: Observable<TripType>;
  private readonly travelClasses$: Observable<TravelClasses>;
  private readonly routeType$: Observable<LocationRouteType>;

  private readonly shortHaulFlightWeights = {
    'fragments.lightTicketWarning': 20,
    'fragments.superLightTicketWarning': 30,
    'fragments.businessLightTicketWarning': 10,
  };

  private readonly longHaulFlightWeights = {
    'fragments.lightTicketWarning': 20,
    'fragments.superLightTicketWarning': 10,
    'fragments.businessLightTicketWarning': 30,
  };

  private readonly superLightFareFamiliesDictionary: TranslationKeyFareFamilyBransDictiounaryItem[] = [
    {
      translationKey: 'fragments.lightTicketWarning',
      brandNames: [
        'Economy Light',
        'Economy Campaign Light',
        'Economy Light Corporate',
        'Premium Economy Light',
        'Premium Economy Light Corporate',
        'Premium Economy Campaign Light',
        'Premium Economy Campaign Light Corporate',
      ],
    },
    {
      translationKey: 'fragments.superLightTicketWarning',
      brandNames: ['Economy Superlight', 'Economy Superlight Campaign', 'Economy Superlight Corporate'],
    },
    {
      translationKey: 'fragments.businessLightTicketWarning',
      brandNames: ['Business Campaign Light', 'Business Light Corporate', 'Business Light'],
    },
  ];

  private subscriptions = new Subscription();

  constructor(
    private store$: Store<BookingAndAppState>,
    private languageService: LanguageService,
    private locationsService: LocationsService,
    private router: Router
  ) {
    this.booking$ = this.store$.pipe(bookingFlow());
    this.dates$ = this.store$.pipe(globalBookingDates());
    this.step$ = this.store$.pipe(bookingStep());
    this.step$
      .pipe(
        distinctUntilChanged((prev, curr) => prev.id === curr.id),
        pairwise(),
        map(([previousStep]) => previousStep)
      )
      // eslint-disable-next-line rxjs/no-ignored-subscription
      .subscribe((step) => this.previousStep$.next(step));
    this.paxAmount$ = this.store$.pipe(globalBookingPaxAmount());
    this.tripType$ = this.store$.pipe(globalBookingTripType());
    this.travelClasses$ = this.store$.pipe(globalBookingTravelClasses());
    this.locations$ = this.store$.pipe(bookingLocations());
    this.routeType$ = this.store$.pipe(globalBookingRouteType());

    this.fetchLocationInformationBasedOnSelections();
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  get step(): Observable<Step> {
    return this.step$;
  }

  get currentStep(): Step {
    return snapshot(this.store$.pipe(bookingStep()));
  }

  get previousStep(): Observable<Step> {
    return this.previousStep$.asObservable();
  }

  get flow(): BookingFlow {
    return snapshot(this.store$.pipe(bookingFlow()));
  }

  get globalBookingSelections(): GlobalBookingState {
    return snapshot(this.store$.pipe(globalBookingSelections()));
  }

  get tripDates(): GlobalBookingTripDates {
    return snapshot(this.store$.pipe(globalBookingDates()));
  }

  get observable(): Observable<BookingFlow> {
    return this.booking$;
  }

  get locations(): Observable<BookingTripLocations[]> {
    // TODO: clear (in reducer) and fetch locations if missing, e.g., when changing language
    // see https://github.com/FinnairOyj/finnair-com-ux/issues/2208
    return this.locations$;
  }

  get selectedDates(): Observable<GlobalBookingTripDates> {
    return this.dates$;
  }

  get paxAmount(): Observable<PaxAmount> {
    return this.paxAmount$;
  }

  get travelClasses(): Observable<TravelClasses> {
    return this.travelClasses$.pipe(finShare());
  }

  get tripType(): Observable<TripType> {
    return this.tripType$.pipe(finShare());
  }

  get offerListParams(): OfferListFetchParams {
    return snapshot(this.store$.pipe(lastRequestParams())) ?? snapshot(this.store$.pipe(offerLastRequestParams()));
  }

  get routeType(): Observable<LocationRouteType> {
    return this.routeType$.pipe(finShare());
  }

  setLocations(locations: BookingLocationCodes[]): void {
    this.store$.dispatch(BookingActions.setLocations({ locations }));
    this.store$.dispatch(
      GlobalBookingActions.setLocations({
        locations: locations.map((l) => ({
          origin: { locationCode: l.originLocationCode },
          destination: { locationCode: l.destinationLocationCode },
        })) as LocationPair[],
      })
    );
  }

  setRouteType(routeType: LocationRouteType): void {
    this.store$.dispatch(GlobalBookingActions.setRouteType({ routeType }));
  }

  setDates(dates: GlobalBookingTripDates): void {
    if (!equals(dates, snapshot(this.store$.pipe(globalBookingDates())))) {
      this.store$.dispatch(GlobalBookingActions.setDates({ dates }));
    }
  }

  setStep({ step, onSuccessPost = noop, skipRouteChange = false }: SetStepSignature) {
    this.goToStep({ step, skipRouteChange, onSuccessPost });
  }

  prevStep(): void {
    const nextStep = this.currentStepIndex - 1;

    if (nextStep >= 0 && this.currentStepIndex !== this.getStepIndex(BOOKING_STEPS.PURCHASE_SUCCESS)) {
      this.setStep({
        step: BOOKING_STEPS_ARRAY[nextStep],
      });
    }
  }

  nextStep(): void {
    const nextStep = this.currentStepIndex + 1;

    if (nextStep < BOOKING_STEPS_ARRAY.length) {
      this.setStep({
        step: BOOKING_STEPS_ARRAY[nextStep],
      });
    }
  }

  isFirstStep(): Observable<boolean> {
    return this.step.pipe(map((s) => s.id === BOOKING_STEPS.ENTRY.id));
  }

  setPaxAmount(paxAmount: PaxAmount): void {
    if (!equals(paxAmount, snapshot(this.store$.pipe(globalBookingPaxAmount())))) {
      this.store$.dispatch(GlobalBookingActions.setPaxAmount({ paxAmount }));
    }
  }

  increasePaxAmountField(field: string, value: number): void {
    this.store$.dispatch(GlobalBookingActions.increasePaxAmountField({ field, increment: value }));
  }

  decreasePaxAmountField(field: string, value: number): void {
    this.store$.dispatch(GlobalBookingActions.decreasePaxAmountField({ field, decrement: value }));
  }

  setTripType(tripType: TripType): void {
    if (tripType !== snapshot(this.store$.pipe(globalBookingTripType()))) {
      this.store$.dispatch(GlobalBookingActions.setTripType({ tripType }));
    }
  }

  selectTravelClass(travelClass: GlobalBookingTravelClass): void {
    this.store$.dispatch(GlobalBookingActions.setTravelClass({ travelClass }));
  }

  updateTripTypeBasedOnQueryParams(queryParams: Params): void {
    const tripType = queryParams[BookingQueryParams.tripType];

    if (AVAILABLE_TRIP_TYPES.includes(tripType)) {
      this.setTripType(tripType);
    }
  }

  getContentUrlTranslationKeySuperLightModalWindow(isShortHaulFlight: boolean): string {
    const selectedFareFamiliesBrands = snapshot(this.store$.pipe(selectFareFamiliesBrands()));

    return this.superLightFareFamiliesDictionary
      .filter((dictionary) => {
        if (selectedFareFamiliesBrands.filter((brand) => dictionary.brandNames.includes(brand)).length > 0) {
          return dictionary;
        }
      })
      .reduce((prev, current) => {
        const weights = isShortHaulFlight ? this.shortHaulFlightWeights : this.longHaulFlightWeights;
        return weights[prev.translationKey] > weights[current.translationKey] ? prev : current;
      }, {} as TranslationKeyFareFamilyBransDictiounaryItem)?.translationKey;
  }

  private get currentStepIndex(): number {
    return this.getStepIndex(this.currentStep);
  }

  private goToStep({ step, onSuccessPost = noop, skipRouteChange = false }: SetStepSignature): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      // Clear cart if returning to flight selection or earlier from a later phase
      if (
        step.phase <= BOOKING_STEPS.FLIGHT_SELECTION.phase &&
        this.currentStep &&
        this.currentStep.phase > step.phase
      ) {
        this.store$.dispatch(CartActions.reset());
      }

      if (!skipRouteChange) {
        this.router
          .navigateByUrl(
            getBookingStepLink(
              this.languageService.langValue,
              step,
              createParamsForStep(step, this.flow, this.globalBookingSelections, this.offerListParams)
            )
          )
          .then((b) => {
            if (b) {
              this.store$.dispatch(BookingActions.setStep({ step }));
              resolve();
            } else {
              reject();
            }
          })
          .catch(reject);
      } else {
        this.store$.dispatch(BookingActions.setStep({ step }));
        resolve();
      }
    })
      .then(() => onSuccessPost(step))
      .catch((_error) => {});
  }

  private getStepIndex(step: Step): number {
    return BOOKING_STEPS_ARRAY.findIndex((s) => s.id === step.id);
  }

  private fetchLocationInformationBasedOnSelections() {
    this.subscriptions.add(
      this.store$
        .pipe(
          bookingLocationCodes(),
          filter((locations: BookingLocationCodes[]) => {
            return !!locations?.length;
          }),
          distinctUntilChanged()
        )
        .subscribe((locations: BookingLocationCodes[]) => {
          const locationCodes = locations
            .flatMap((location) => [location.originLocationCode, location.destinationLocationCode])
            .filter(unique)
            .filter(Boolean);

          locationCodes.forEach((code) => {
            this.locationsService.triggerLocationFetch(code);
          });
        })
    );
  }
}
